import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, api, errMsg } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarX2, FileText, Loader2, MapPin, Pencil, Plus, QrCode, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "", description: "", horario_id: "", regras_path: null, attachments: [],
  taxa_uso: "", antecedencia_min: 0, bloqueios: [],
};
const EMPTY_BLOQUEIO = { inicio: "", fim: "", motivo: "" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AreasPage() {
  const navigate = useNavigate();
  const [areas, setAreas] = useState(null);
  const [horarios, setHorarios] = useState([]);
  const [open, setOpen] = useState(false);
  const [noHorarioOpen, setNoHorarioOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/tenant/areas").then(({ data }) => setAreas(data)).catch(() => setAreas([]));
  useEffect(() => {
    load();
    api.get("/tenant/horarios").then(({ data }) => setHorarios(data)).catch(() => setHorarios([]));
  }, []);

  const openNew = () => {
    if (!horarios.length) {
      setNoHorarioOpen(true);
      return;
    }
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name, description: a.description || "",
      horario_id: a.horario_id || "", regras_path: a.regras_path || null,
      attachments: a.attachments || [],
      taxa_uso: a.taxa_uso ?? "", antecedencia_min: a.antecedencia_min ?? 0,
      bloqueios: (a.bloqueios || []).map((b) => ({
        inicio: toLocalInput(b.inicio), fim: toLocalInput(b.fim), motivo: b.motivo || "",
      })),
    });
    setOpen(true);
  };

  const pickRegras = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        paths.push(data.storage_path);
      }
      setForm((f) => ({
        ...f,
        regras_path: f.regras_path || paths[0] || null,
        attachments: f.regras_path ? [...(f.attachments || []), ...paths] : [...(f.attachments || []), ...paths.slice(1)],
      }));
      toast.success(files.length > 1 ? "Arquivos anexados" : "Arquivo anexado");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const setBloqueio = (i, field, value) => {
    setForm((f) => ({ ...f, bloqueios: f.bloqueios.map((b, bi) => (bi === i ? { ...b, [field]: value } : b)) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, description: form.description,
      horario_id: form.horario_id, regras_path: form.regras_path,
      attachments: form.attachments || [],
      taxa_uso: form.taxa_uso === "" ? null : Number(form.taxa_uso),
      antecedencia_min: Number(form.antecedencia_min) || 0,
      bloqueios: form.bloqueios
        .filter((b) => b.inicio || b.fim || b.motivo)
        .map((b) => ({
          inicio: b.inicio ? new Date(b.inicio).toISOString() : "",
          fim: b.fim ? new Date(b.fim).toISOString() : "",
          motivo: b.motivo,
        })),
    };
    try {
      if (editing) {
        await api.patch(`/tenant/areas/${editing.id}`, payload);
        toast.success("Área atualizada");
      } else {
        await api.post("/tenant/areas", payload);
        toast.success("Área cadastrada");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteArea = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tenant/areas/${deleteTarget.id}`);
      toast.success("Área excluída");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDeleting(false);
    }
  };

  const horarioLabel = (id) => horarios.find((h) => h.id === id)?.descricao || "—";

  if (!areas) return <Spinner />;

  return (
    <div data-testid="areas-page">
      <PageHeader title="Áreas e Locais" subtitle="Catalogue os espaços para abertura de chamados via QR Code e reservas">
        <Button data-testid="new-area-button" onClick={openNew}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> Nova Área
        </Button>
      </PageHeader>

      {areas.length === 0 ? (
        <EmptyState icon={MapPin} title="Nenhuma área cadastrada"
          subtitle="Cadastre espaços como Espaço Gourmet, Piscina, SPA, Casa de Máquinas, Salão de Festas…" />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Código</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Local</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Disponibilidade</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Taxa de uso</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Descrição</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Restrição</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {areas.map((a) => (
                <tr key={a.id} data-testid={`area-row-${a.id}`}
                  className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-200">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{a.code}</td>
                  <td className="px-4 py-3 text-zinc-100 font-medium">{a.name}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs" data-testid={`area-horario-${a.id}`}>{horarioLabel(a.horario_id)}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs" data-testid={`area-taxa-${a.id}`}>
                    {a.taxa_uso ? `R$ ${Number(a.taxa_uso).toFixed(2).replace(".", ",")}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs max-w-64 truncate">{a.description || "—"}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`area-restricao-${a.id}`}>
                    <span className={a.bloqueios?.length ? "text-amber-400 font-medium" : "text-zinc-500"}
                      title={a.bloqueios?.length ? `${a.bloqueios.length} bloqueio(s) de datas` : "Sem bloqueios"}>
                      {a.bloqueios?.length ? "Sim" : "Não"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1">
                      <Button data-testid={`area-edit-${a.id}`} variant="ghost" size="icon" title="Alterar área"
                        onClick={() => openEdit(a)}
                        className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button data-testid={`area-delete-${a.id}`} variant="ghost" size="icon" title="Excluir área"
                        onClick={() => setDeleteTarget(a)}
                        className="h-8 w-8 text-zinc-400 hover:text-rose-400 hover:bg-zinc-800">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button data-testid={`area-qrcode-${a.id}`} variant="ghost" size="icon"
                        title="Abrir card PDF para impressão"
                        onClick={() => window.open(`${API}/tenant/areas/${a.id}/card.pdf`, "_blank")}
                        className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                        <QrCode className="h-4 w-4" />
                      </Button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="area-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">{editing ? "Editar Área / Local" : "Nova Área / Local"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              O código é gerado automaticamente e o card com QR Code fica disponível para impressão.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="area-create-form">
            <div>
              <Label className="text-xs text-zinc-400">Nome do local</Label>
              <Input data-testid="area-name-input" required value={form.name} maxLength={60}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex.: Espaço Gourmet"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Descrição (opcional)</Label>
              <Textarea data-testid="area-description-input" value={form.description} rows={2} maxLength={300}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Ex.: Área comum do 2º pavimento, ao lado da churrasqueira"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Disponibilidade de Horário *</Label>
              <Select value={form.horario_id} onValueChange={(v) => setForm({ ...form, horario_id: v })}>
                <SelectTrigger data-testid="area-horario-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                  <SelectValue placeholder="Selecione a faixa de horário…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {horarios.map((h) => (
                    <SelectItem key={h.id} data-testid={`area-horario-option-${h.id}`} value={h.id}>{h.codigo} — {h.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!horarios.length && (
                <p className="text-xs text-amber-400 mt-1.5" data-testid="area-no-horario-warning">
                  É necessário cadastrar pelo menos 1 horário de funcionamento em Cadastros → Horários.
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Taxa de uso (R$)</Label>
                <Input data-testid="area-taxa-input" type="number" min="0" step="0.01" value={form.taxa_uso}
                  onChange={(e) => setForm({ ...form, taxa_uso: e.target.value })}
                  placeholder="0,00"
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Antecedência mínima para reserva (minutos)</Label>
                <Input data-testid="area-antecedencia-input" type="number" min="0" step="1" value={form.antecedencia_min}
                  onChange={(e) => setForm({ ...form, antecedencia_min: e.target.value })}
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
              </div>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="area-regras-section">
              <p className="text-xs font-medium text-zinc-400">Checklist, Regras e outros arquivos</p>
              <div className="flex flex-wrap gap-2">
                {form.regras_path && (
                  <span className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 text-zinc-300 rounded-full px-2.5 py-1"
                    data-testid="area-regras-chip">
                    <FileText className="h-3 w-3" /> Regras/Checklist
                    <button type="button" data-testid="area-regras-remove" title="Remover arquivo"
                      onClick={() => setForm((f) => ({ ...f, regras_path: null }))}
                      className="hover:text-rose-400 transition-colors duration-150">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {(form.attachments || []).map((path, i) => (
                  <span key={path} className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 text-zinc-300 rounded-full px-2.5 py-1"
                    data-testid={`area-anexo-chip-${i}`}>
                    <FileText className="h-3 w-3" /> Documento {i + 2}
                    <button type="button" data-testid={`area-anexo-remove-${i}`} title="Remover arquivo"
                      onClick={() => setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((_, idx) => idx !== i) }))}
                      className="hover:text-rose-400 transition-colors duration-150">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" multiple
                data-testid="area-regras-file-input" onChange={pickRegras} />
              <div>
                <Button type="button" data-testid="area-regras-button" variant="outline" disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileText className="h-4 w-4 mr-1.5" />}
                  Enviar arquivos
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="area-bloqueios-section">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-zinc-400 flex items-center gap-1.5">
                  <CalendarX2 className="h-3.5 w-3.5" /> Bloqueio de datas
                </p>
                <Button type="button" data-testid="area-bloqueio-add" variant="outline" size="sm"
                  onClick={() => setForm((f) => ({ ...f, bloqueios: [...f.bloqueios, { ...EMPTY_BLOQUEIO }] }))}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md h-7 text-xs">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Incluir
                </Button>
              </div>
              {form.bloqueios.length === 0 ? (
                <p className="text-[11px] text-zinc-600">Nenhum bloqueio — o espaço segue a disponibilidade de horário.</p>
              ) : form.bloqueios.map((b, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end border-t border-zinc-800/60 pt-2"
                  data-testid={`area-bloqueio-${i}`}>
                  <div>
                    <Label className="text-[11px] text-zinc-500">Início</Label>
                    <Input type="datetime-local" data-testid={`area-bloqueio-inicio-${i}`} value={b.inicio}
                      onChange={(e) => setBloqueio(i, "inicio", e.target.value)}
                      className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md text-xs [color-scheme:dark]" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-500">Fim</Label>
                    <Input type="datetime-local" data-testid={`area-bloqueio-fim-${i}`} value={b.fim}
                      onChange={(e) => setBloqueio(i, "fim", e.target.value)}
                      className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md text-xs [color-scheme:dark]" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-500">Motivo</Label>
                    <Input data-testid={`area-bloqueio-motivo-${i}`} value={b.motivo} maxLength={150}
                      onChange={(e) => setBloqueio(i, "motivo", e.target.value)}
                      placeholder="Ex.: Manutenção programada"
                      className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 rounded-md text-xs" />
                  </div>
                  <Button type="button" data-testid={`area-bloqueio-remove-${i}`} variant="ghost" size="icon" title="Remover bloqueio"
                    onClick={() => setForm((f) => ({ ...f, bloqueios: f.bloqueios.filter((_, bi) => bi !== i) }))}
                    className="h-8 w-8 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" data-testid="area-cancel-button" variant="ghost" onClick={() => setOpen(false)}
                className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
                Cancelar
              </Button>
              <Button data-testid="area-create-submit" type="submit" disabled={saving || uploading || !form.horario_id}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Salvar alterações" : "Cadastrar Área"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={noHorarioOpen} onOpenChange={setNoHorarioOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="no-horario-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Nenhum horário cadastrado</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Para incluir Áreas e Espaços é necessário cadastrar pelo menos 1 horário de funcionamento, que define a disponibilidade para reservas.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button data-testid="no-horario-cancel" variant="ghost" onClick={() => setNoHorarioOpen(false)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Cancelar
            </Button>
            <Button data-testid="no-horario-goto" onClick={() => { setNoHorarioOpen(false); navigate("/app/horarios"); }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
              Ir para Horários
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Excluir área</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Tem certeza que deseja excluir <span className="text-zinc-200 font-medium">{deleteTarget?.name}</span> ({deleteTarget?.code})? Chamados já abertos manterão o nome do local registrado.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2" data-testid="delete-area-dialog">
            <Button data-testid="delete-area-cancel" variant="ghost" onClick={() => setDeleteTarget(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Cancelar
            </Button>
            <Button data-testid="delete-area-confirm" onClick={deleteArea} disabled={deleting}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-md transition-colors duration-200">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
